import { strToU8, zipSync } from "fflate"

import type { ZipEntrySource, ZipRequest, ZipResponse } from "types/csv.types"

const zipOnMainThread = (files: ZipEntrySource[]): Uint8Array => {
  const entries: Record<string, Uint8Array> = {}
  files.forEach((file) => {
    entries[file.filename] = strToU8(file.text)
  })
  return zipSync(entries)
}

/**
 * Zips the CSVs in a worker Vite emits as a real chunk under `/assets/`, so it is
 * same-origin and the production CSP (`script-src 'self'`, no `worker-src`) allows it.
 *
 * fflate's async `zip` cannot be used here: it builds its worker from a `blob:` URL,
 * which that CSP blocks, and it registers no `onerror`, so the blocked worker never
 * answered and the download hung forever instead of failing. Hence the explicit
 * `onerror`/`onmessageerror` handling below.
 *
 * Any worker failure — CSP, a chunk that failed to load, a browser without module
 * worker support — falls back to zipping here. The tab janks for a moment, but the
 * download always finishes or reports an error. Note the `new URL(...)` form is
 * required: `?worker&inline` would put us back on a `blob:` worker.
 */
export const zipCsvFiles = (files: ZipEntrySource[]): Promise<Uint8Array> => {
  let worker: Worker
  try {
    worker = new Worker(new URL("./zipWorker.ts", import.meta.url), {
      type: "module",
    })
  } catch {
    // No `Worker` at all (jsdom) or the constructor refused the URL. Keep the failure
    // a rejection like the fallback below, rather than a synchronous throw.
    try {
      return Promise.resolve(zipOnMainThread(files))
    } catch (error) {
      return Promise.reject(error)
    }
  }

  return new Promise<Uint8Array>((resolve, reject) => {
    let settled = false
    const finish = (complete: () => void) => {
      if (settled) return
      settled = true
      worker.terminate()
      complete()
    }
    worker.onmessage = ({ data }: MessageEvent<ZipResponse>) => {
      finish(() => (data.ok ? resolve(data.bytes) : reject(new Error(data.error))))
    }
    // `zipOnMainThread` runs inside the worker's error handler, not inside this
    // executor, so a throw there would escape unhandled and leave the promise pending
    // forever — the exact hang this worker was meant to remove. Reject instead.
    const fallBack = () =>
      finish(() => {
        try {
          resolve(zipOnMainThread(files))
        } catch (error) {
          reject(error)
        }
      })
    worker.onerror = fallBack
    worker.onmessageerror = fallBack
    const request: ZipRequest = { files }
    worker.postMessage(request)
  })
}
